import { useEffect, useRef, useState } from "react";
import { ImageSquare, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Card, Field, Grid, ReadOnlyNote, btnPrimary } from "./shared";
import { BRAND_FONTS } from "../../lib/brandFonts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [["logo", "Logo del ristorante"], ["cover", "Immagine di copertina"]];
const COLORS = [["primary", "Colore primario"], ["secondary", "Colore secondario"], ["accent", "Colore accento"]];

function FontPreview({ fontKey }) {
  const f = BRAND_FONTS.find((x) => x.key === fontKey);
  useEffect(() => {
    if (!f?.url) return;
    let l = document.getElementById("brand-font-preview-link");
    if (!l) {
      l = document.createElement("link");
      l.id = "brand-font-preview-link"; l.rel = "stylesheet";
      document.head.appendChild(l);
    }
    l.href = f.url;
  }, [fontKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f?.url) return null;
  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-zinc-50" data-testid="brand-font-preview">
      <div style={{ fontFamily: f.display }} className="text-xl font-bold text-zinc-900">Il titolo del tuo locale</div>
      <div style={{ fontFamily: f.body }} className="text-sm text-zinc-600 mt-1">Questo è il testo di esempio del corpo: prenota il tuo tavolo per sabato sera.</div>
    </div>
  );
}

export default function BrandMediaSection({ settings, onSave, saving, readOnly }) {
  const [brand, setBrand] = useState(null);
  const [colors, setColors] = useState(settings.brand_colors || {});
  const [font, setFont] = useState(settings.brand_style?.font || "");
  const [busy, setBusy] = useState("");
  const refs = { logo: useRef(), cover: useRef() };

  const load = () => api.get("/settings/brand").then((r) => setBrand(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("File troppo grande (max 2MB)"); return; }
    setBusy(slot);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/settings/brand/assets/${slot}`, { data: reader.result, content_type: file.type });
        toast.success("Immagine caricata");
        load();
      } catch (err) { toast.error(apiError(err)); }
      finally { setBusy(""); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (slot) => {
    try {
      await api.delete(`/settings/brand/assets/${slot}`);
      toast.success("Immagine rimossa");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="setsection-brand">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-xs text-sky-800" data-testid="brand-scope-note">
        Il logo, i colori e il font del ristorante vengono usati <b>esclusivamente nel front-end pubblico</b> (pagina di prenotazione, landing moduli web, comunicazioni). L'applicazione gestionale mantiene sempre l'identità Dishenda.
      </div>

      <Card title="Media del ristorante" desc="PNG/JPG/SVG, max 2MB. Anteprima con sfondo trasparente." testid="brand-media-card">
        <Grid>
          {SLOTS.map(([slot, label]) => (
            <div key={slot} className="space-y-2" data-testid={`brand-slot-${slot}`}>
              <span className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
              <div className="h-28 rounded-lg border border-zinc-200 flex items-center justify-center overflow-hidden"
                style={{ backgroundImage: "repeating-conic-gradient(#f4f4f5 0% 25%, #fff 0% 50%)", backgroundSize: "16px 16px" }}>
                {brand?.assets?.[slot]
                  ? <img src={`${BACKEND}${brand.assets[slot]}?t=${Date.now()}`} alt={label} className="max-h-24 max-w-full object-contain" data-testid={`brand-preview-${slot}`} />
                  : <ImageSquare size={28} className="text-zinc-300" />}
              </div>
              {!readOnly && (
                <div className="flex gap-2">
                  <input ref={refs[slot]} type="file" accept="image/*" className="hidden"
                    onChange={(e) => upload(slot, e.target.files[0])} data-testid={`brand-file-${slot}`} />
                  <button onClick={() => refs[slot].current.click()} disabled={busy === slot}
                    className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold disabled:opacity-50"
                    data-testid={`brand-upload-${slot}`}>
                    {busy === slot ? "Caricamento..." : brand?.assets?.[slot] ? "Sostituisci" : "Carica"}
                  </button>
                  {brand?.assets?.[slot] && (
                    <button onClick={() => remove(slot)}
                      className="h-9 px-3 rounded-md border border-rose-200 text-rose-600 text-xs font-semibold flex items-center gap-1"
                      data-testid={`brand-delete-${slot}`}><Trash size={13} /> Rimuovi</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Grid>
      </Card>

      <Card title="Colori del brand" desc="Palette usata nelle pagine pubbliche del ristorante." testid="brand-colors-card">
        <Grid>
          {COLORS.map(([k, label]) => (
            <Field key={k} label={label}>
              <div className="flex gap-2 items-center">
                <input type="color" value={colors[k] || "#18181B"}
                  onChange={(e) => setColors({ ...colors, [k]: e.target.value })}
                  className="w-11 h-11 rounded-md border border-zinc-300 cursor-pointer" data-testid={`brand-color-${k}`} />
                <input value={colors[k] || ""} onChange={(e) => setColors({ ...colors, [k]: e.target.value })}
                  className="h-11 px-3 rounded-md border border-zinc-300 text-sm font-mono w-28"
                  data-testid={`brand-hex-${k}`} />
              </div>
            </Field>
          ))}
        </Grid>
      </Card>

      <Card title="Font delle pagine pubbliche" desc="Carattere usato nelle landing dei moduli web e nelle pagine pubbliche del ristorante." testid="brand-font-card">
        <div className="space-y-3 max-w-md">
          <select value={font} onChange={(e) => setFont(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white"
            data-testid="brand-font-select">
            {BRAND_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <FontPreview fontKey={font} />
        </div>
      </Card>

      {readOnly ? <ReadOnlyNote /> : (
        <button disabled={saving} onClick={() => onSave({ brand_colors: colors, brand_style: { font } })}
          className={btnPrimary} data-testid="brand-save">Salva Brand & Media</button>
      )}
    </div>
  );
}
